/* globals echarts, widgetL10n */
/**
 * Chart widget used to display graphical charts
 * charcoal/admin/widget/graph
 *
 * Require:
 * - jQuery
 * - echarts {@link https://ecomfe.github.io/echarts-doc/public/en/api.html#echarts}
 *
 * @param  {Object}  opts Options for widget
 */

import * as echarts from 'echarts';
import _, {debounce, forEach, isInteger} from 'lodash'
import Mustache from 'mustache'

const DEFAULT_PIE_CHART_OPTIONS = {
    startAngle: 90
}
const LABEL_MAP = {
    Jan: {axis: 'JA', tooltip: 'Janvier'},
    Feb: {axis: 'FE', tooltip: 'Février'},
    Mar: {axis: 'MA', tooltip: 'Mars'},
    Apr: {axis: 'AV', tooltip: 'Avril'},
    May: {axis: 'MA', tooltip: 'Mai'},
    Jun: {axis: 'JN', tooltip: 'Juin'},
    Jul: {axis: 'JL', tooltip: 'Juillet'},
    Aug: {axis: 'AU', tooltip: 'Août'},
    Sep: {axis: 'SE', tooltip: 'Septembre'},
    Oct: {axis: 'OC', tooltip: 'Octobre'},
    Nov: {axis: 'NO', tooltip: 'Novembre'},
    Dec: {axis: 'DE', tooltip: 'Décembre'}
}

const EVENT_NAMESPACE = '.pajr-chart'

const MAP_TO_AXIS_LABEL = function (value) {
    return LABEL_MAP[value]?.axis ?? value
}

const MAP_TO_TOOLTIP_LABEL = function (value) {
    return LABEL_MAP[value]?.tooltip ?? value
}

const FORMAT_NUMBER = function (value) {
    return parseInt(value).toLocaleString('fr')
}

/**
 * Represents a Chart widget, extending the `Charcoal.Admin.Widget` class to integrate with ECharts for visualizations.
 */
class Chart extends Charcoal.Admin.Widget {
    constructor(opts) {
        super(opts);
        this.targetSeries = null
        this.chartSeries = null
        this.regionId = opts.regionId ?? null;
        this.mrcId = opts.mrcId ?? null;
        this.abortController = null;
    }

    // Used to store the current MRC and region IDs for breadcrumbs filtering
    regionId = null;
    mrcId = null;
    abortController = null;

    breadcrumbPrefixes(ident) {
        const prefixes = {
            region: this.regionId ? `region/${(this.regionId)}/` : null,
            mrc: this.mrcId ? `mrc/${(this.mrcId)}/` : null,
        }

        return ident ? prefixes[ident] : prefixes;
    }

    init() {
        super.init();
        this.$widget = this.element();
        this.darkMode = this.widgetOptions().chart.theme === 'dark';

        this.createChart();

        $(window).on('resize', () => {
            this.chart.resize();
            this.$widget.trigger('pajr:chart-resized', this.chart.getWidth());
        });

        $('.js-graph-define-target', this.$widget).on('click' + EVENT_NAMESPACE, (event) => {
            const dataset = JSON.parse(event.currentTarget.dataset['buttonOptions']);
            this.triggerDefineTargetModal(dataset, (response) => {
                if (response.reload) {
                    this.destroy()
                    this.init()
                }
            })
        })

        // $(this.$widget.parents('.card').find('.js-graph-refresh'))
        $('.js-graph-refresh', this.$widget).on('click' + EVENT_NAMESPACE, () => {
            this.refresh()
        });

        // Trigger breadcrumb registration from widget to ensure parentality
        // Reload the chart when the breadcrumbs are changed
        // Apply api endpoint prefixes
        this.$widget.trigger('pajr:chart-breadcrumb:register', (data) => {
            const {mrc, region} = data;

            this.mrcId = mrc?.id;
            this.regionId = region?.id;
            echarts.dispose(this.chart)
            this.createChart();
        })

        this.registerAddRemoveActions()
    }

    registerAddRemoveActions() {
        const incrementalEndpoints = $('.js-incremental-endpoint', this.$widget)

        if (!incrementalEndpoints.length) return;

        forEach(incrementalEndpoints, (el) => {
            const $el = $(el)
            const {dataset} = $el.get(0)
            const {options} = dataset;

            let optionsAsJSON = options ? JSON.parse(options) ?? null : null;

            $el.on('click' + EVENT_NAMESPACE, async () => {
                const request = new ApiRequest(Object.assign(optionsAsJSON, {
                    fetchOptions: {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json; charset=UTF-8', // Inform the server of the data type
                        },
                        body: JSON.stringify({"ident": optionsAsJSON.ident})
                    },
                    signal: this.abortController?.signal
                }), this)

                const result = await request.fetch()

                if (result.response.success) {
                    this.destroy()
                    this.init()
                }
            })
        })
    }

    destroy() {
        // Abort any pending requests
        if (this.abortController) {
            this.abortController.abort();
        }

        // Delete the echart instance
        echarts.dispose(this.chart)

        // Remove event listeners
        $(window).off('resize');
        $('.js-graph-define-target', this.$widget).off('click' + EVENT_NAMESPACE);
        $('.js-graph-refresh', this.$widget).off('click' + EVENT_NAMESPACE);
        $('.js-incremental-endpoint', this.$widget).off('click' + EVENT_NAMESPACE);
        this.$widget.off('pajr');
        this.$widget.off('*' + EVENT_NAMESPACE)
        super.destroy();
    }

    /**
     * Initializes and creates a chart using the ECharts library.
     * The chart is rendered inside a specific container, with options set via the echartsOptions method.
     * Displays a loading indicator while handling API requests, which are resolved asynchronously.
     *
     * @return {void} Does not return a value.
     */
    createChart() {
        // Abort any existing requests
        if (this.abortController) {
            this.abortController.abort();
        }

        // Create new AbortController for this request
        this.abortController = new AbortController();

        this.chart = echarts.init(this.$widget.find('.js-graph-container').get(0));
        this.showLoading();

        this.chart.setOption(this.echartsOptions());

        this.handleApiRequests(this.abortController.signal).then(() => {
            this.chart.hideLoading();
        }).catch((error) => {
            if (error.name !== 'AbortError') {
                this.chart.hideLoading();
            }
        })
    }

    showLoading() {
        const opts = {
            text: widgetL10n.loading,
        }

        if (this.darkMode) {
            Object.assign(opts, {
                maskColor: '#2E2E2E',
                textColor: '#eee'
            });
        }

        this.chart.showLoading(opts);
    }

    /**
     * Handles API requests for fetching data, processing series, and updating chart configurations.
     * This method extracts API configurations, validates requests, fetches data, processes the series,
     * and updates the chart with the fetched and processed data. It also sets up an event listener
     * for legend selection changes to dynamically update the chart based on user interactions.
     *
     * @return {Promise<void>} A promise that resolves when all API requests are processed and the chart is updated.
     */
    async handleApiRequests(signal) {
        try {
            // Extracted reusable configurations
            const apiConfigs = this.widgetOptions().api;

            const {
                series: seriesConfig,
                targets: targetsConfig,
            } = apiConfigs

            // Extracted valid API requests
            const apiRequests = {
                series: this.getValidApiRequests(seriesConfig),
                targets: this.getValidApiRequests(targetsConfig),
            };

            const groupedPromises = async function () {
                return {
                    targets: await Promise.all(apiRequests.targets.map(request => request.fetch(signal))),
                    series: await Promise.all(apiRequests.series.map(request => request.fetch(signal)))
                };
            }
            // const groupedPromises = apiRequests.map(async (request) => await request.fetch())

            // Fetch the data from all API requests
            const apiResponses = await groupedPromises();
            // const apiResponses = await Promise.all(apiRequests.map(request => request.fetch()));
            // Extract series from API data
            const series = apiResponses.series.flatMap(request => request.getSeries());
            const targetSeries = apiResponses.targets
                .flatMap(request => request.getSeries())
                .filter(item => {
                    // Making sure the targets have values
                    return item.data.some(data => {
                        const value = parseInt(data.value)
                        return isInteger(value) && value !== 0;
                    })
                });

            // Create a mapped targetSeries container for easy access by name.
            this.targetSeries = _.keyBy(targetSeries, 'id')
            // Process series data
            this.processChartSeries(series);
            this.chartSeries = [...targetSeries, ...series]
            // Update chart options
            this.chart.setOption({
                series: this.chartSeries
            });

            // Event listener for legend selection changes
            this.chart.on('legendselectchanged', (eventParams) => {
                const selectedLegendStates = eventParams.selected;

                // Update the selected states in series
                this.updateSelectedStates(this.chartSeries, selectedLegendStates);

                // Process and update chart options
                this.processChartSeries(series);
                this.chart.setOption({
                    series: this.chartSeries
                });
            });
        } catch (error) {
            console.error('Error while handling API requests:', error); // Improved descriptive logging
        }
    }

    // Update series items state based on legend selection
    // This is used for target calculation
    updateSelectedStates(series, selectedLegendStates) {
        series.forEach(seriesItem => {
            seriesItem.data.forEach(dataItem => {
                if (!_.isPlainObject(dataItem)) {
                    seriesItem.data.selected = selectedLegendStates[dataItem];
                    return;
                }
                dataItem.selected = selectedLegendStates[dataItem.name];
            });
        });
    }

    /**
     * Processes all `pie` chart series within the provided series array.
     * @param {Array<Object>} series - The chart series array.
     */
    processChartSeries(series) {
        // A bit hackish, but it's used for only one chart thus far
        // Custom tooltip formatter for bar charts with stacks
        if (this.widgetOptions()?.chartIdent === 'migrationSucceedParallelByRegionChart') {
            return this.processMigrationSucceedParallelByRegionChartSeries(series)
        }

        for (const seriesItem of series) {
            if (seriesItem.type === 'pie') {
                this.processSinglePieChartSeries(series, seriesItem);
            }

            if (seriesItem.type === 'list') {
                this.processListSeriesGraphics(series)
                return
            }

            if (seriesItem.type === 'bar' || seriesItem.type === 'line') {
                // Mapping response series data to chart axis labels
                const labels = seriesItem.data
                    .map(item => item[0])

                this.chart.setOption({
                    xAxis: {
                        data: labels,
                        axisLabel: {
                            formatter: (params) => {
                                return MAP_TO_AXIS_LABEL(params)
                            }
                        }
                    }
                })

                // We only need the first series for the chart axis labels
                break;
            }
        }

        // Generate chart graphics from series data
        this.processChartSeriesGraphics(series)
        this.parseSeriesTooltip(series)
    }

    parseSeriesTooltip(series) {
        return series.map(seriesItem => {
            if (seriesItem?.tooltip === undefined || seriesItem?.tooltip?.formatter === undefined) {
                seriesItem.tooltip = {
                    formatter: this.createTooltipFormatter()
                }
            }
        })
    }

    processMigrationSucceedParallelByRegionChartSeries(series) {

        // Series Tooltips
        for (const seriesItem of series) {
            seriesItem.tooltip = seriesItem.tooltip || {}
            seriesItem.tooltip.formatter = params => {
                const {name, seriesName, value, marker} = params
                const yValue = value[1]

                return `
                    <div>${name} <span class="font-weight-bold ml-3 float-right">(${seriesItem.stack})</span></div>
                    <div>${marker} ${seriesName} <span class="font-weight-bold ml-3 float-right">${yValue}</span></div>
                `
            }
        }

        // Graphical elements
        const graphElements = []
        const sumByYears = _.reduce(series, (acc, item) => {
            acc[item.stack] = acc[item.stack] || 0
            acc[item.stack] += _.sum(item.data.map(item => parseInt(item[1])))

            return acc
        }, {})

        const sumByYearsEntries = _.entries(sumByYears)
        sumByYearsEntries.forEach((value, key) => {
            graphElements.push([
                `${value[0]} {value|${value[1]}}`,
            ])
        })

        // Update width on resize
        this.$widget.on('pajr:chart-resized', debounce((event, width) => {
            this.chart.setOption({
                graphic: {
                    id: 'total',
                    style: {
                        width: width * 0.2,
                    }
                }
            })
        }, 200))

        this.chart.setOption({
            graphic: {
                id: 'total',
                $action: 'merge',
                type: 'text',
                transition: ['x', 'y', 'style'],
                enterFrom: {
                    y: 100,
                    style: {opacity: 0}
                },
                leaveTo: {style: {opacity: 0}},
                right: '5%',
                // top: '10%',
                y: 75,
                style: {
                    width: this.chart.getWidth() * 0.2,
                    overflow: 'break',
                    text: [
                        `{title|Totaux des 3 dernères années}`,
                        `{spacing|}`,
                        _.join(graphElements, `\n{hr|}\n`)
                    ].join('\n'),
                    rich: {
                        title: {
                            fontSize: 18,
                            fontWeight: 'bold',
                            lineHeight: 18,
                        },
                        spacing: {
                            width: '100%',
                            height: 50
                        },
                        value: {
                            fontSize: 24,
                            fontWeight: 'bold',
                            align: 'right',
                            padding: [10, 0],
                            fill: '#6092f5',
                        },
                        hr: {
                            borderColor: '#3C3C41',
                            borderWidth: 1,
                            width: '100%',
                            height: 0,
                        }
                    },
                    fill: '#3C3C41',
                    font: 'bold 16px Roboto',
                }
            }
        })
    }

    /**
     * Creates and returns a graphic container element with total and optional target elements for the chart.
     * The container adjusts based on the provided options including target value and percentage.
     *
     * @param {Object} options - The options for creating the graphic elements.
     * @param {Object} options.series - Series information to determine chart bounds and positioning.
     * @param {number} options.totalValue - The total value to be displayed.
     * @param {number} [options.targetValue=null] - The target value to be displayed. If null, the target element is removed.
     * @param {number} [options.percentage=null] - A percentage value used for color interpolation if a target value is provided.
     * @param {string} [options.id] - A unique identifier for the graphic elements. If not provided, one is generated automatically.
     * @return {Object} The graphic container configuration object with the created total and optional target elements.
     */
    createTotalGraphicElements(options) {
        // Handle default optiopns
        const {
            series,
            totalValue,
            targetValue = null,
            percentage = null,
            id = _.uniqueId(),
        } = options

        const totalGraphicOptions = {
            ...{
                showTitle: false,
                position: 'inside'
            },
            ...series?.totalGraphicOptions ?? {}
        }

        const inside = totalGraphicOptions.position === 'inside'

        const graphicId = function ($name) {
            return $name + '_' + id
        }

        const
            chartModel = this.chart.getModel().getOption().graphic,
            hasTargetElement = chartModel?.some(el => el.elements.some(child => child.id === graphicId('target')));

        // Extracted helper for creating the target element
        const createTargetElement = (value) => ({
            id: graphicId('target'),
            info: {seriesId: id},
            type: 'text',
            $action: 'merge',
            transition: ['style'],
            enterFrom: {style: {opacity: 0}},
            leaveTo: {style: {opacity: 0}},
            ignore: false,
            top: 36,
            z: 100,
            style: {
                text: FORMAT_NUMBER(value),
                textAlign: 'center',
                fill: this.darkMode ? '#fff' : '#3C3C41',
                font: '16px Roboto',
            },
        });

        // Extracted helper for creating the "remove target" element
        const createRemoveTargetElement = () => ({
            $action: 'merge',
            ignore: true,
            id: graphicId('target'),
            top: 0,
            enterFrom: {style: {opacity: 0}},
            leaveTo: {style: {opacity: 0}},
        });


        // Determine "target" element logic
        const targetElement = targetValue
            ? createTargetElement(targetValue)
            : !!hasTargetElement
                ? createRemoveTargetElement()
                : null;

        const groupGraphic = {
            id: graphicId('group-1'),
            type: 'group',
            top: inside ? 'center' : 'left',
            left: inside ? 'center' : 'left',
            $action: 'merge',
            transition: ['x', 'y', 'style'],

            children: [
                {
                    id: graphicId('total'),
                    type: 'text',
                    info: {seriesId: id},
                    $action: 'merge',
                    top: inside ? 0 : 32,
                    z: 100,
                    transition: ['x', 'y', 'style'],
                    enterFrom: {style: {opacity: 0}},
                    leaveTo: {style: {opacity: 0}},
                    style: {
                        text: FORMAT_NUMBER(totalValue),
                        textAlign: inside ? 'center' : 'left',

                        fill: targetValue !== null
                            ? interpolateBetweenColors('rgb(196,57,57)', 'rgb(70,171,70)', percentage)
                            : this.darkMode ? '#fff' : '#3C3C41',
                        font: 'bold 32px Roboto',
                    },
                },
                targetElement, // Add target or remove target logic
            ]
        }

        if (totalGraphicOptions.showTitle) {
            groupGraphic.children.unshift({
                type: 'text',
                z: 100,
                transition: ['x', 'y', 'style'],
                enterFrom: {style: {opacity: 0}},
                leaveTo: {style: {opacity: 0}},
                style: {
                    text: 'Total',
                    textAlign: 'left',
                    fill: this.darkMode ? '#fff' : '#3C3C41',
                    font: '16px Roboto',
                },
            })
        }

        let leftPosition,
            topPosition;

        if (inside) {
            leftPosition = series?.left === 'center' ? (100 - series?.width.replace('%', '')) / 2 + '%' : series?.left;
            topPosition = series?.top === 'center' ? (100 - series?.height.replace('%', '')) / 2 + '%' : series?.top;
        } else {
            leftPosition = totalGraphicOptions?.left ?? 15
            topPosition = totalGraphicOptions?.top ?? '10%'
        }


        // Return the full graphic group
        return {
            id: graphicId('total-container'),
            isTotalGraphic: true,
            type: 'group',
            targetValue: targetValue,
            x: 0,
            y: 0,
            left: leftPosition,
            width: series?.width ?? '100%',
            height: series?.height ?? '100%',
            top: topPosition,
            $action: 'merge',
            // Raw seems to better at handling percent-based positioning
            bounding: 'raw',
            children: [
                groupGraphic
            ].filter(Boolean), // Remove null elements
        };
    }


    /**
     * Processes and configures the chart series graphics based on the widget options and series data.
     * This method handles the creation of graphical elements for each series and manages their behavior
     * with respect to hover interactions, if applicable.
     *
     * @param {Array} series - An array of series data objects used to generate graphical elements for the chart.
     * @return {void} - This method does not return a value. It configures the chart graphics directly.
     */
    processChartSeriesGraphics(series) {
        let graphicalElements = []

        // This needs to be explicitly defined in options
        // As of now only a "total" graphic is supported with
        // The total graphic also allows for the target display
        if (this.widgetOptions().totalGraph?.active) {
            graphicalElements = [...graphicalElements, ...series.map(item => this.parseSeriesAsGraphicElements(item))];
        }


        if (this.widgetOptions().totalGraph?.mode === 'hover') {
            graphicalElements = _.each(graphicalElements, (item) => {
                item.ignore = item.ignore || true
                return item
            })

            // Display the last element by default because it's the outer ring on a pie chart. Might want to change later.'
            _.last(graphicalElements).ignore = false

            const updateGraphicHandler = (params) => {
                if (!_.includes(['series', 'legend'], params.componentType)) return

                const source = this.chartSeries[params.seriesIndex]

                const ele = _.each(graphicalElements, (item => {
                    item.ignore = item.isTotalGraphic && item.id !== 'total-container_' + source?.info?.seriesId
                    return item
                }))

                this.chart.setOption({
                    graphic: ele
                })
            }

            this.chart.on('mouseover', updateGraphicHandler);
        }

        if (this.widgetOptions().totalSummaryGraph?.active) {
            graphicalElements.push(this.parseSeriesAsTotalSummaryGraphicElement(series))
        }

        this.chart.setOption({
            graphic: graphicalElements
        });
    }

    processListSeriesGraphics(series) {
        const test = this.widgetOptions().chart.listTemplate
        const data = series.pop().data

        data.forEach((item, index) => {
            item.unshift(`<span style="font-size: 28px; font-weight: bold">${index + 1}</span>`)
        })

        const $container = this.$widget.find('.js-graph-container > div')

        $container.get(0).innerHTML = Mustache.render(test, {
            data: data
        })

        window.requestAnimationFrame(() => {
            this.$widget.find('.js-graph-container > div > table').addClass('show')
        })
    }

    parseSeriesAsTotalSummaryGraphicElement(series) {
        const graphOptions = {
            ...this.widgetOptions().totalSummaryGraph,
            ...series[0]?.totalSummaryGraph ?? {}
        }

        const bigTotal = series.reduce((total, item) => total += this.calculateAggregateValue(item.data) ?? 0, 0)
        const hasTitle = typeof this.widgetOptions().chart?.title === 'string' || this.widgetOptions().chart?.title?.text !== undefined

        const y = graphOptions?.y ? graphOptions?.y : hasTitle ? 100 : 25
        const yOffset = y + 25

        const statTextSize = graphOptions?.statTextSize ?? 32
        const titleTextSize = graphOptions?.titleTextSize ?? 16

        return {
            id: 'total-summary-container',
            type: 'group',
            x: 0,
            y: y,
            left: 5,
            width: '100%',
            height: '100%',
            transition: ['top', 'y', 'style'],
            enterFrom: {
                y: yOffset,
            },
            leaveTo: {
                y: yOffset,
            },
            children: [
                {
                    type: 'text',
                    z: 100,
                    transition: ['x', 'y', 'style'],
                    enterFrom: {style: {opacity: 0}},
                    leaveTo: {style: {opacity: 0}},
                    style: {
                        text: 'Total',
                        textAlign: 'left',
                        fill: this.darkMode ? '#fff' : '#3C3C41',
                        font: `${titleTextSize}px Roboto`,
                    }
                },
                {
                    type: 'text',
                    $action: 'merge',
                    top: statTextSize - 6,
                    z: 100,
                    transition: ['x', 'y', 'style'],
                    enterFrom: {style: {opacity: 0}},
                    leaveTo: {style: {opacity: 0}},
                    style: {
                        text: FORMAT_NUMBER(bigTotal),
                        textAlign: 'left',

                        fill: this.darkMode ? '#fff' : '#3C3C41',
                        font: `bold ${statTextSize}px Roboto`,
                    }
                }
            ]
        }
    }

    /**
     * Parses the given series and its associated target series into graphic elements.
     *
     * @param {Object} series - The series data containing information needed for parsing.
     *                          It should include properties such as `id`, `target`, `data`,
     *                          and optionally an `info` object.
     * @return {Array} An array of graphic elements created based on the series and target data.
     *                 Returns an empty array if required calculations cannot be performed.
     */
    parseSeriesAsGraphicElements(series) {
        const
            target = this.targetSeries[series.target] ?? null,
            targetValue = this.calculateAggregateValue(target?.data),
            total = this.calculateAggregateValue(series.data) ?? 0,
            id = series.id,
            seriesItems = [series]

        if (target) {
            seriesItems.push(target)
        }

        if (targetValue === null || total === null) return [];

        seriesItems.forEach(item => {
            item.info = item?.info || {}
            item.info.seriesId = id
        })

        const options = {
            series: series,
            id: id,
            totalValue: total,
            targetValue: targetValue || null,
            percentage: targetValue && Math.min((total / targetValue) * 100, 100)
        }

        return this.createTotalGraphicElements(options)
    }

    /**
     * Processes a single `pie` chart series and calculates its endAngle, tooltip, etc.
     * @param {Array<Object>} series - The chart series array (used to resolve target data).
     * @param {Object} seriesItem - The individual chart series item being processed.
     */
    processSinglePieChartSeries(series, seriesItem) {
        if (!seriesItem.hasOwnProperty('target')) return;

        const targetName = seriesItem.target;
        const target = this.targetSeries[targetName];

        const targetValue = this.calculateAggregateValue(target?.data);
        const dataValue = this.calculateAggregateValue(seriesItem.data);

        const startAngle = seriesItem.startAngle || DEFAULT_PIE_CHART_OPTIONS.startAngle;
        const endAngle = startAngle - (dataValue / targetValue) * 360;

        // Update series item with calculated values
        Object.assign(seriesItem, {
            endAngle: endAngle,
            tooltip: {
                formatter: this.createTooltipFormatter(targetValue),
            }
        })
    }

    /**
     * Calculates the aggregate value (sum) of the provided data array.
     * @param {Array<Object|number>} data - The array of data items to aggregate.
     * @returns {number} - The aggregate sum of the values.
     */
    calculateAggregateValue(data = []) {
        return data
            .map(item => parseInt(item?.selected === false ? 0 : item.value || item, 10))
            .reduce((sum, value) => sum + value, 0); // Sum with default accumulator
    }

    /**
     * Creates a tooltip formatter for a given target value.
     * @param {number} targetValue - The target value used for percentage calculations.
     * @returns {Function} - The tooltip formatter function.
     */
    createTooltipFormatter(targetValue = null) {
        return function (params) {
            const percent = targetValue ? (params.value / targetValue) * 100 : params.percent;
            const percentString = percent ? ` (${percent?.toFixed()}%)` : '';

            return `
                ${params.marker} ${params.name}: <b class="float-right ml-3">${FORMAT_NUMBER(params.value)}${percentString}</b>
            `;
        };
    }

    /**
     * Filters valid API configurations and creates ApiRequest instances.
     * @param {Array<Object>} apiConfigs - The API configuration array.
     * @returns {Array<ApiRequest>} - An array of ApiRequest instances.
     */
    getValidApiRequests(apiConfigs) {
        return apiConfigs
            .filter(config => config) // Filter valid configurations
            .map(config => new ApiRequest(config, this)); // Create ApiRequest instances
    }

    widgetOptions() {
        return {
            height: this.opts().data.height,
            totalGraph: this.opts().data.totalGraph,
            totalSummaryGraph: this.opts().data.totalSummaryGraph,
            chart: this.opts().data.chart,
            chartIdent: this.opts().data.chartIdent,
            button: this.opts().data.button,
            api: this.opts().data.api,
        }
    }

    parseChartTitle(title, index = null) {
        // Keep a copy of the original title config
        this.orinalTitleConfig = this.orinalTitleConfig ?? _.cloneDeep(title);
        let titleClone = null

        if (_.isArray(title)) {
            return title.map((item, index) => this.parseChartTitle(item, index))
        }

        // When the text element width is negative, it means we want the width to be based on the chart width.
        // This allows adding padding to the right for the menu button, for example.
        // It also means to width will be responsive
        if (title?.textStyle?.width < 1) {
            titleClone = _.cloneDeep(title);

            if (title?.textStyle?.width < 0) {
                titleClone.textStyle.width = this.chart.getWidth() + titleClone.textStyle.width;
            } else {
                // Fractional width, so we need to multiply by the chart width
                titleClone.textStyle.width = titleClone.textStyle.width * this.chart.getWidth();
            }

            this.chartTitleListener = this.chartTitleListener || this.$widget.on('pajr:chart-resized', debounce((event, width) => {
                this.chart.setOption({
                    title: this.parseChartTitle(index ? this.orinalTitleConfig[index] : this.orinalTitleConfig)
                })
            }, 50))
        }

        return titleClone ?? title;
    }

    echartsOptions() {
        const chartData = this.widgetOptions().chart
        let options = {
            title: this.parseChartTitle(chartData.title),
            legend: chartData.legend,
            color: chartData.color,
            animation: chartData.animation,
            tooltip: {
                ...chartData.tooltip,
                formatter: (params) => {
                    if (!_.isArray(params)) {
                        return `
                            ${params.marker} ${params.name}: <b class="float-right ml-3">${FORMAT_NUMBER(params.value)}</b>
                        `;
                    }
                    const totalValues = params.reduce((sum, param) => sum + param.value[1], 0);

                    return MAP_TO_TOOLTIP_LABEL(params[0].name) + params.map(series => {
                        series.value = series.value[1]
                        series.name = series.seriesName
                        const formattedSeries = this.createTooltipFormatter(totalValues)(series)
                        return `<div>${formattedSeries}</div>`
                    }).join('');
                }
            },
            toolbox: {
                show: true,
                showTitle: false,
                feature: {
                    saveAsImage: {
                        show: false,
                        pixelRatio: 2,
                    },
                    dataView: {
                        // Example for custom button to download data as CSV
                        // https://codepen.io/mikechen2017/embed/mjEQPE/?height=265&theme-id=0&default-tab=js,result&embed-version=2
                        show: false,
                        optionToContent: function (opt) {
                            const series = opt.series;
                            const chartType = series[0].type;
                            let dataContent = '';
                            series.forEach(seriesItem => {
                                let table = '<table class="table table-sm table-bordered"><thead><tr>';

                                if (chartType === 'pie') {
                                    if (seriesItem.name) {
                                        table += '<th scope="col">' + seriesItem.name + '</th>';
                                    }
                                    seriesItem.data.forEach(dataItem => {
                                        table += '<th scope="col">' + dataItem.name + '</th>';
                                    });
                                    table += '</thead><tbody><tr>';
                                    if (seriesItem.name) {
                                        table += '<td scope="col"></td>';
                                    }
                                    seriesItem.data.forEach(dataItem => {
                                        table += '<td>' + dataItem.value + '</td>';
                                    });
                                } else if (chartType === 'bar' || chartType === 'line') {
                                    opt.xAxis.forEach(xAxis => {
                                        if (seriesItem.name) {
                                            table += '<th scope="col">' + seriesItem.name + '</th>';
                                        }
                                        xAxis.data.forEach(label => {
                                            table += '<th scope="col">' + label + '</th>';
                                        });
                                    });
                                    table += '</thead><tbody><tr>';
                                    if (seriesItem.name) {
                                        table += '<td scope="col"></td>';
                                    }
                                    seriesItem.data.forEach(value => {
                                        table += '<td>' + value + '</td>';
                                    });
                                }

                                table += '</tr></tbody></table>';
                                dataContent += table;
                            });
                            return dataContent;
                        }
                    }
                }
            },
            calculable: false,
            series: chartData.series
        };

        if (this.darkMode) {
            Object.assign(options, {
                backgroundColor: '#2E2E2E',
                darkMode: true,
            })
        } else {
            options.backgroundColor = '#fff';
        }

        if (chartData.grid) {
            options.grid = chartData.grid;
        }

        if (chartData.xAxis) {
            options.xAxis = chartData.xAxis;
        }

        if (chartData.yAxis) {
            options.yAxis = chartData.yAxis;
        }

        return options;
    }

    triggerDefineTargetModal(dataset, callback) {
        // dataset.form_ident = 'paj/model/mrc/quick'
        dataset.region = this.regionId
        dataset.mrc = this.mrcId
        let reload = false

        const data = {
            title: "Définir la cible",
            size: BootstrapDialog.SIZE_WIDE,
            cssClass: '-quick-form',
            dialog_options: {
                onhide: function () {
                    callback({
                        return: false,
                        reload: reload
                    });
                }
            },
            widget_type: 'charcoal/admin/widget/paj/forms/target-quick-form',
            with_data: true,
            widget_options: dataset
        }

        this.chartDialog(data, (response) => {
            if (!response.widget_id) {
                return false;
            }

            Charcoal.Admin.manager().add_widget({
                id: response.widget_id,
                type: 'charcoal/admin/widget/quick-form',
                data: dataset,
                obj_id: response.widget_data.obj_id,
                save_callback: function (response) {
                    reload = true
                    BootstrapDialog.closeAll();
                }
            })

            // Instead of using the default `Charcoal.Admin.manager().render`,
            // We only render the newly added widget
            this.renderComponent(response.widget_id);
        })
    }

    renderComponent(id) {
        const manager = Charcoal.Admin.manager()
        const newWidget = manager.components['widgets'].find(function (component) {
            return component.id === id;
        });

        const index = _.findIndex(manager.components['widgets'], widget => widget.id === id);
        const component = new Charcoal.Admin[newWidget.ident](newWidget);
        manager.components['widgets'][index] = component;

        Charcoal.Admin.Widget.call(component, newWidget);
        component.init();

        manager.components['property_inputs'].map((prop, i) => {
            if (prop instanceof Charcoal.Admin.Property) {
                if (typeof prop.destroy === 'function') {
                    prop.destroy();
                    prop.init();
                }
                return;
            }

            const component = new Charcoal.Admin[prop.ident](prop);
            manager.components['property_inputs'][i] = component;
            Charcoal.Admin.Widget.call(component, prop);
            component.init();
        });
    }

    chartDialog(dialog_opts, callback) {
        var title = dialog_opts.title || '',
            type = dialog_opts.type || BootstrapDialog.TYPE_DEFAULT,
            size = dialog_opts.size || BootstrapDialog.SIZE_NORMAL,
            cssClass = dialog_opts.cssClass || '',
            showHeader = dialog_opts.showHeader || true,
            showFooter = dialog_opts.showFooter || true,
            userOptions = dialog_opts.dialog_options || {};

        delete dialog_opts.title;
        delete dialog_opts.type;
        delete dialog_opts.size;
        delete dialog_opts.cssClass;
        delete dialog_opts.dialog_options;

        var defaultOptions = {
            title: title,
            type: type,
            size: size,
            cssClass: cssClass,
            nl2br: false,
            showHeader: showHeader,
            showFooter: showFooter
        };

        var dialogOptions = $.extend({}, defaultOptions, userOptions);
        var alertTemplate = '<div class="alert alert-{type}" role="alert">{text}</div>';

        dialogOptions.onshown = function (dialog) {
            var xhr,
                url = Charcoal.Admin.admin_url() + 'widget/load',
                data = dialog_opts;

            xhr = $.ajax({
                method: 'POST',
                url: url,
                data: JSON.stringify(data),
                dataType: 'json',
                contentType: 'application/json'
            });

            xhr
                .then(function (response, textStatus, jqXHR) {
                    if (!response || !response.success) {
                        if (response.feedbacks) {
                            return $.Deferred().reject(jqXHR, textStatus, response.feedbacks);
                        } else {
                            return $.Deferred().reject(jqXHR, textStatus, widgetL10n.loadingFailed);
                        }
                    }

                    return $.Deferred().resolve(response, textStatus, jqXHR);
                })
                .done(function (response/*, textStatus, jqXHR*/) {
                    dialog.setMessage(response.widget_html);

                    if (typeof callback === 'function') {
                        callback(response);
                    }

                    $('[data-toggle="tooltip"]', dialog.getModalBody()).tooltip();
                })
                .fail(function (jqXHR, textStatus, errorThrown) {
                    dialog.setType(BootstrapDialog.TYPE_DANGER);
                    dialog.setMessage(widgetL10n.loadingFailed);

                    var errorHtml = '';

                    if ($.type(errorThrown) === 'string') {
                        if (jqXHR.responseJSON && jqXHR.responseJSON.feedbacks) {
                            errorThrown = jqXHR.responseJSON.feedbacks;
                        }
                    }

                    if ($.isArray(errorThrown)) {
                        $.each(errorThrown, function (i, error) {
                            if (error.message) {
                                if (error.level === 'error') {
                                    error.level = 'danger';
                                }
                                errorHtml += alertTemplate.replaceMap({
                                    '{type}': error.level,
                                    '{text}': error.message
                                });
                            }
                        });
                    } else if ($.type(errorThrown) === 'string') {
                        errorHtml = alertTemplate.replaceMap({
                            '{type}': 'danger',
                            '{text}': errorThrown
                        });
                    }

                    if (errorHtml) {
                        dialog.setMessage(errorHtml);
                    }

                    $('[data-toggle="tooltip"]', dialog.getModalBody()).tooltip();
                });

            // Charcoal.Admin.manager().render();
        };

        dialogOptions.message = function (dialog) {
            var $message = $(
                alertTemplate.replaceMap({
                    '{type}': 'warning',
                    '{text}': widgetL10n.loading
                })
            );

            if (!showHeader) {
                dialog.getModalHeader().addClass('d-none');
            }

            if (!showFooter) {
                dialog.getModalFooter().addClass('d-none');
            }

            dialog.getModalBody().on(
                'click.charcoal.bs.dialog',
                '[data-dismiss="dialog"]',
                {dialog: dialog},
                function (event) {
                    event.data.dialog.close();
                }
            );

            return $message;
        };

        return new BootstrapDialog.show(dialogOptions);
    }
}

Charcoal.Admin.Widget_Paj_Chart = Chart


/**
 * Handles API requests and processes the fetched data.
 */
class ApiRequest {
    widgetRef = null                // Reference to the widget instance
    endpoint = null;                // API endpoint URL
    allowEndpointPrefixes = null;   // Array of allowed endpoint prefixes
    responseHandler = null;         // Instance of ResponseHandler
    response = null;                // Fetched response data

    /**
     * Constructor to initialize ApiRequest.
     * @param {Object} options - Configuration options for the API request.
     * @param {string} options.endpoint - The API endpoint URL.
     * @param {Object} [options.response] - Response handling configuration.
     */
    constructor(options, widgetRef) {
        const {
            endpoint,
            allowEndpointPrefixes,
            apiBasePath,
            fetchOptions,
            ...rest
        } = options;
        this.widgetRef = widgetRef;
        this.allowEndpointPrefixes = allowEndpointPrefixes;
        this.apiBasePath = apiBasePath;
        this.endpoint = this.applyEndpointPrefixes(endpoint);
        this.responseHandler = new ResponseHandler(rest);
        this.fetchOptions = fetchOptions || {};
    }

    /**
     * Fetches data from the API endpoint and stores the response.
     * @returns {Promise<ApiRequest>} - The instance of ApiRequest with the fetched response.
     */
    async fetch(signal = null) {
        const fetchOptions = signal ? {...this.fetchOption, ...signal} : this.fetchOptions;

        if (this.endpoint instanceof Object) {
            const requests = [];
            this.response = {}; // init response container.
            for (let ep in this.endpoint) {
                const request = async () => {
                    const response = await fetch(this.endpoint[ep], fetchOptions);
                    this.response[ep] = await response.json();
                    return response;
                }
                requests.push(request());
            }
            const responses = await Promise.all(requests);
            const dataPromises = responses.map(async response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status} from ${response.url}`);
                }
            });

            await Promise.all(dataPromises);

            return this;
        }

        const response = await fetch(this.endpoint, fetchOptions);
        this.response = await response.json();
        return this;
    }

    applyEndpointPrefixes(endpoint, endpointIdent = '') {
        // Early bail
        if (!this.allowEndpointPrefixes) return endpoint;
        if (endpoint instanceof Object) {
            const out = []
            for (let ep in endpoint) {
                out[ep] = this.applyEndpointPrefixes(endpoint[ep], ep);
            }
            return out;
        }

        let prefixes

        if (this.allowEndpointPrefixes instanceof Array) {
            prefixes = this.allowEndpointPrefixes;
        } else if (this.allowEndpointPrefixes instanceof Object) {
            prefixes = endpointIdent
                ? this.allowEndpointPrefixes[endpointIdent]
                : null;
        }
        if (!prefixes) return endpoint;

        let out = ''
        for (let prefix of prefixes) {
            let breadcrumbPrefix = this.widgetRef.breadcrumbPrefixes(prefix)
            if (breadcrumbPrefix) out += breadcrumbPrefix
        }

        if (this.apiBasePath) {
            endpoint = endpoint.replace(this.apiBasePath + '/', '')

            return this.apiBasePath + '/' + out + endpoint;
        }

        return out + endpoint;
    }

    /**
     * Processes the API response through the ResponseHandler and retrieves chart series.
     * @returns {Array<Object>} - Array of series objects for charts.
     */
    getSeries() {
        const chartSeries = [...this.responseHandler.handleResponse(this.response)];
        return chartSeries.map((series) => series.getSeries());
    }
}

/**
 * Handles the processing of API responses and maps them to chart series.
 */
class ResponseHandler {
    responseConfig = null; // Configuration object for response handling
    seriesData = {};       // Default configuration for series data

    /**
     * Constructs an instance of the class with the provided response configuration and series data.
     *
     * @param {Object} config - The configuration object for the constructor.
     * @param {Object} config.responseConfig - The response configuration object.
     * @param {...Object} seriesData - Additional series data to be stored.
     * @return {void}
     */
    constructor({responseConfig, ...seriesData}) {
        if (responseConfig) {
            this.responseConfig = Object.assign({}, responseConfig);
        }
        this.seriesData = seriesData;
    }

    /**
     * Processes the API response and generates chart series objects.
     * @generator
     * @param {Object} response - The API response data.
     * @yields {ChartSeries} - Configured chart series object.
     */
    * handleResponse(response) {
        const seriesConfigurations = this.responseConfig?.series || [this.seriesData];
        delete this.responseConfig?.series;

        // If we supply an object configuration for series, the mapping process treat the response array items as series objects.
        if (typeof seriesConfigurations === 'object' && !Array.isArray(seriesConfigurations)) {
            for (const responseSeries of response) {
                yield new ChartSeries({
                    ...this.seriesData,
                    ...this.responseConfig,
                    ...seriesConfigurations
                }).processResponse(responseSeries);
            }

            return
        }

        for (const series of (seriesConfigurations)) {
            yield new ChartSeries({
                ...this.seriesData,
                ...this.responseConfig,
                ...series
            }).processResponse(response);
        }
    }
}

/**
 * Represents a chart series and processes response data to populate it.
 */
class ChartSeries {
    categoryFromKey = null; // Key to map categories from the response
    map = {};
    // dataFromKey = null;     // Key to extract data points from the response
    series = {};            // Configured series object

    MAPPING_REGEX = new RegExp(/\$\{([^}]*)}/g)

    /**
     * Constructs an instance of the class with a map and additional properties.
     *
     * @param {Object} options - The options object for the constructor.
     * @param {Object} options.map - The map object to be associated with the instance.
     * @param {...Object} rest - Additional properties to be assigned to the instance.
     * @return {Object} The constructed instance of the class.
     */
    constructor({map, ...rest}) {
        this.map = map;
        this.series = rest;

        this.series.id = this.series.id || _.uniqueId('')

        return this;
    }

    /**
     * Processes the API response and maps it to the chart series.
     * @param {Object | Array} response - API response data to process.
     * @returns {ChartSeries} - Returns the instance for chaining.
     */
    processResponse(response) {
        const seriesData = this.mapResponse(response);

        Object.assign(this.series, seriesData);
        return this;
    }

    /**
     * Maps the API response data according to the mapping rules.
     * The method handles both object and array responses.
     * @param {Object | Array} response - API response data.
     * @returns {Object} - Mapped response data.
     */
    mapResponse(response) {
        // Initialize an empty object to store mapped data.
        const responseMapping = {};

        // Handle array responses by recursively mapping each item.
        if (Array.isArray(response)) {
            response.forEach((responseItem) => {
                this.mergeMappedData(
                    responseMapping,
                    this.mapResponse(responseItem)
                );
            });
            return responseMapping;
        }

        // Process each mapping rule and apply it to the current response.
        Object.entries(this.map).forEach(([key, mappingRules]) => {
            responseMapping[key] = this.renderResponse(mappingRules, response);
        });

        return responseMapping;
    }

    renderResponse(map, response) {
        if (Array.isArray(map)) {
            return map.map((item) => this.renderResponse(item, response))
        }

        if (_.isObject(map)) {
            return _.mapValues(map, (value) => this.renderResponse(value, response))
        }

        // Prevent parsing for Boolean, Number, etc.
        if (typeof map !== 'string') {
            return map
        }

        // Allows inserting object/array in place of the regex tag.
        // Replace the entire string value with an object.
        let checkForArrayMapping
        while ((checkForArrayMapping = this.MAPPING_REGEX.exec(map)) !== null) {
            const mappedResponse = checkForArrayMapping ? response[checkForArrayMapping[1]] : null
            if (mappedResponse && typeof (mappedResponse) === 'object') {
                return mappedResponse
            }
        }

        return _.replace(map, this.MAPPING_REGEX, (match, p1) => {
            return _.get(response, p1)
        })

    }

    /**
     * Merges new mapped data into the existing response mapping,
     * ensuring that array values are concatenated.
     * @param {Object} target - The object to merge data into.
     * @param {Object} source - The new mapped data to merge.
     */
    mergeMappedData(target, source) {
        Object.entries(source).forEach(([key, value]) => {
            if (!Array.isArray(target[key])) {
                target[key] = [];
            }
            target[key].push(value);
        });
    }

    /**
     * Retrieves the configured series object.
     * @returns {Object} - The chart series configuration.
     */
    getSeries() {
        return this.series;
    }
}


/**
 * Parses a color string and converts it into an object representing RGB values.
 * The input string can either be in HEX format (#RRGGBB or #RGB) or RGB format (rgb(r, g, b)).
 *
 * @param {string} colorString - The color string to parse, in either HEX or RGB format.
 * @return {{r: number, g: number, b: number} | null} An object with `r`, `g`, and `b` properties representing the red, green, and blue components of the color, or null if the input string cannot be parsed.
 */
function parseColorString(colorString) {
    // Check if the color is in HEX format (e.g., #RRGGBB or #RGB)
    if (colorString.startsWith('#')) {
        let hex = colorString.slice(1);
        if (hex.length === 3) {
            // Convert shorthand HEX (#RGB) to full HEX (#RRGGBB)
            hex = hex.split('').map((char) => char + char).join('');
        }
        const intValue = parseInt(hex, 16);
        return {
            r: (intValue >> 16) & 255,
            g: (intValue >> 8) & 255,
            b: intValue & 255,
        };
    }

    // Check if the color is in RGB format (e.g., rgb(255, 255, 255))
    const rgbRegex = /rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)/;
    const match = colorString.match(rgbRegex);
    if (match) {
        return {
            r: parseInt(match[1], 10),
            g: parseInt(match[2], 10),
            b: parseInt(match[3], 10),
        };
    }

    // If unable to parse the color string, return null
    return null;
}

/**
 * Interpolates between two colors based on a percentage.
 *
 * @param {string} fromColor - The starting color in a valid string format (e.g., "rgb(255,0,0)" or "#FF0000").
 * @param {string} toColor - The target color in a valid string format (e.g., "rgb(0,255,0)" or "#00FF00").
 * @param {number} percent - The percentage (0 to 100) indicating the interpolation point between the two colors.
 * @return {string} The interpolated color in the "rgb(r, g, b)" format.
 */
function interpolateBetweenColors(fromColor, toColor, percent) {
    fromColor = parseColorString(fromColor)
    toColor = parseColorString(toColor)

    const delta = percent / 100;
    const r = Math.round(fromColor.r + (toColor.r - fromColor.r) * delta);
    const g = Math.round(fromColor.g + (toColor.g - fromColor.g) * delta);
    const b = Math.round(fromColor.b + (toColor.b - fromColor.b) * delta);

    return `rgb(${r}, ${g}, ${b})`;
}
